import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { chainPoApi } from '../services/api';
import { formatDateTime, formatDateDMY } from '../lib/utils';
import {
  Plus, Trash2, Send, Download, PackageCheck, X, Link2, Bell,
  CheckCircle2, Clock, AlertTriangle, Users, FileText, Hash,
} from 'lucide-react';

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 border-slate-300',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300 border-blue-300',
  partially_received: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300 border-amber-300',
  fully_received: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-300',
  closed: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200 border-neutral-400',
};
const STATUS_LABEL = {
  draft: 'Draft',
  sent: 'Sent',
  partially_received: 'Partial',
  fully_received: 'Fully Received',
  closed: 'Closed',
};

const emptyItem = { chain_name: '', karat: '18kt', length_inches: '', quantity: '', notes: '' };

export default function ChainPurchaseOrders() {
  const [tab, setTab] = useState('pos');
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(false);
  // Dialog states
  const [vendorDlg, setVendorDlg] = useState(null); // null | {mode:'create'|'edit', data}
  const [poDlg, setPoDlg] = useState(null);         // null | {mode:'create'|'view'|'receive', data}
  const [hookDlg, setHookDlg] = useState(null);     // null | {vendor}

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [v, p] = await Promise.all([chainPoApi.listVendors(), chainPoApi.listPos()]);
      setVendors(v.data);
      setPos(p.data);
    } catch (e) {
      toast.error('Failed to load chain data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <div className="min-h-screen">
      <Header title="Chain Purchase Orders" />
      <div className="p-4 md:p-6 space-y-4">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi icon={Users} label="Vendors" value={vendors.length} tone="slate" />
          <Kpi icon={FileText} label="Open POs" value={pos.filter(p => ['sent','partially_received'].includes(p.status)).length} tone="amber" />
          <Kpi icon={AlertTriangle} label="Overdue" value={pos.filter(p => p.is_overdue).length} tone="rose" />
          <Kpi icon={CheckCircle2} label="Fully Received" value={pos.filter(p => p.status === 'fully_received' || p.status === 'closed').length} tone="emerald" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <TabsList>
              <TabsTrigger value="pos" data-testid="tab-pos">Purchase Orders</TabsTrigger>
              <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              {tab === 'pos' && (
                <Button onClick={() => setPoDlg({ mode: 'create', data: null })} data-testid="new-po-btn">
                  <Plus className="h-4 w-4 mr-1" /> New PO
                </Button>
              )}
              {tab === 'vendors' && (
                <Button onClick={() => setVendorDlg({ mode: 'create', data: {} })} data-testid="new-vendor-btn">
                  <Plus className="h-4 w-4 mr-1" /> New Vendor
                </Button>
              )}
            </div>
          </div>

          <TabsContent value="pos" className="mt-3">
            <POList pos={pos} loading={loading} onOpen={(p) => setPoDlg({ mode: 'view', data: p })} />
          </TabsContent>
          <TabsContent value="vendors" className="mt-3">
            <VendorList
              vendors={vendors}
              onEdit={(v) => setVendorDlg({ mode: 'edit', data: v })}
              onHooks={(v) => setHookDlg({ vendor: v })}
              onDelete={async (v) => {
                if (!window.confirm(`Delete vendor "${v.name}"?`)) return;
                try {
                  await chainPoApi.deleteVendor(v.id);
                  toast.success('Vendor deleted'); fetchAll();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
              }}
            />
          </TabsContent>
        </Tabs>
      </div>

      {vendorDlg && (
        <VendorDialog
          initial={vendorDlg.data}
          mode={vendorDlg.mode}
          onClose={() => setVendorDlg(null)}
          onSaved={() => { setVendorDlg(null); fetchAll(); }}
        />
      )}
      {poDlg && (
        <PODialog
          initial={poDlg.data}
          mode={poDlg.mode}
          vendors={vendors}
          onClose={() => setPoDlg(null)}
          onSaved={() => { setPoDlg(null); fetchAll(); }}
        />
      )}
      {hookDlg && (
        <SpringHookDialog vendor={hookDlg.vendor} onClose={() => { setHookDlg(null); fetchAll(); }} />
      )}
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, tone }) => {
  const toneMap = {
    slate: 'text-slate-600 dark:text-slate-300', amber: 'text-amber-600 dark:text-amber-300',
    rose: 'text-rose-600 dark:text-rose-300', emerald: 'text-emerald-600 dark:text-emerald-300',
  };
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-3">
        <Icon className={`h-6 w-6 ${toneMap[tone]}`} />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const POList = ({ pos, loading, onOpen }) => {
  if (loading) return <Card><CardContent className="p-8 text-center text-muted-foreground">Loading…</CardContent></Card>;
  if (pos.length === 0) return <Card><CardContent className="p-8 text-center text-muted-foreground"><FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />No purchase orders yet</CardContent></Card>;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">PO #</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Delivery</th>
              <th className="px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {pos.map((p) => (
              <tr key={p.id} className="border-t border-border cursor-pointer hover:bg-muted/40" onClick={() => onOpen(p)} data-testid={`po-row-${p.id}`}>
                <td className="px-3 py-2 font-semibold flex items-center gap-1"><Hash className="h-3 w-3 text-muted-foreground" />{p.po_number}</td>
                <td className="px-3 py-2">{p.vendor_name || '—'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.total_qty}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.received_qty} / <span className="text-muted-foreground">{p.pending_qty} pending</span></td>
                <td className="px-3 py-2">
                  <Badge className={`${STATUS_STYLES[p.status]} border text-[10px]`}>{STATUS_LABEL[p.status]}</Badge>
                  {p.is_overdue && <Badge className="ml-1 text-[10px] bg-rose-100 text-rose-700 border-rose-300">Overdue</Badge>}
                </td>
                <td className="px-3 py-2 text-xs">{formatDateDMY(p.expected_delivery_date) || '—'}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{formatDateDMY(p.created_at) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const VendorList = ({ vendors, onEdit, onHooks, onDelete }) => {
  if (vendors.length === 0) return <Card><CardContent className="p-8 text-center text-muted-foreground"><Users className="h-10 w-10 mx-auto mb-2 opacity-30" />No vendors yet</CardContent></Card>;
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {vendors.map((v) => (
        <Card key={v.id} data-testid={`vendor-card-${v.id}`}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold">{v.name}</h3>
                <p className="text-xs text-muted-foreground">{v.contact_person || '—'}{v.phone ? ` · ${v.phone}` : ''}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge variant={v.is_active ? 'default' : 'outline'} className="text-[10px]">{v.is_active ? 'Active' : 'Inactive'}</Badge>
                {v.whatsapp_group_id && (
                  <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700 dark:text-emerald-400" title={`Group: ${v.whatsapp_group_id}`} data-testid={`vendor-wa-group-badge-${v.id}`}>
                    WA Group
                  </Badge>
                )}
              </div>
            </div>
            {v.email && <p className="text-xs">{v.email}</p>}
            {v.address && <p className="text-xs text-muted-foreground line-clamp-2">{v.address}</p>}
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
              <div><p className="text-[9px] uppercase text-muted-foreground">POs</p><p className="text-sm font-semibold">{v.total_pos}</p></div>
              <div><p className="text-[9px] uppercase text-muted-foreground">Open</p><p className="text-sm font-semibold">{v.open_pos}</p></div>
              <div><p className="text-[9px] uppercase text-muted-foreground">Hooks Issued</p><p className="text-sm font-semibold">{v.spring_hooks_issued}</p></div>
            </div>
            <div className="flex gap-1.5 pt-1">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => onEdit(v)} data-testid={`edit-vendor-${v.id}`}>Edit</Button>
              <Button size="sm" variant="outline" className="flex-1" onClick={() => onHooks(v)} data-testid={`hooks-vendor-${v.id}`}><Link2 className="h-3.5 w-3.5 mr-1" />Hooks</Button>
              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => onDelete(v)} data-testid={`del-vendor-${v.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ─────── Vendor Dialog ───────
const VendorDialog = ({ initial, mode, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: initial?.name || '', phone: initial?.phone || '', email: initial?.email || '',
    address: initial?.address || '', contact_person: initial?.contact_person || '', notes: initial?.notes || '',
    whatsapp_group_id: initial?.whatsapp_group_id || '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'create') await chainPoApi.createVendor(f);
      else await chainPoApi.updateVendor(initial.id, f);
      toast.success(mode === 'create' ? 'Vendor created' : 'Vendor updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{mode === 'create' ? 'New Chain Vendor' : `Edit ${initial?.name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Vendor Name *"><Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} data-testid="vendor-name-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact Person"><Input value={f.contact_person} onChange={(e) => setF({...f, contact_person: e.target.value})} /></Field>
            <Field label="Phone (WhatsApp)"><Input value={f.phone} onChange={(e) => setF({...f, phone: e.target.value})} placeholder="9199..." data-testid="vendor-phone-input" /></Field>
          </div>
          <Field label="Email"><Input value={f.email} onChange={(e) => setF({...f, email: e.target.value})} /></Field>
          <Field label="Address"><Textarea rows={2} value={f.address} onChange={(e) => setF({...f, address: e.target.value})} /></Field>
          <Field label="WhatsApp Group ID">
            <Input
              value={f.whatsapp_group_id}
              onChange={(e) => setF({...f, whatsapp_group_id: e.target.value})}
              placeholder="clientCustomGroupId set in DoubleTick"
              data-testid="vendor-wa-group-id-input"
              className="font-mono text-xs"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Optional. If set, all Chain PO WhatsApp messages for this vendor will be sent to the group instead of their personal number. Falls back to the phone above if the group send fails. Create the group inside DoubleTick and paste the <code className="text-[10px]">clientCustomGroupId</code> here.
            </p>
          </Field>
          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-vendor-btn">{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────── PO Dialog (create + view + receive) ───────
const PODialog = ({ initial, mode, vendors, onClose, onSaved }) => {
  const [f, setF] = useState({
    vendor_id: initial?.vendor_id || (vendors[0]?.id || ''),
    items: initial?.items?.map(i => ({ ...i })) || [{ ...emptyItem }],
    expected_delivery_date: initial?.expected_delivery_date || '',
    notes: initial?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveNote, setReceiveNote] = useState('');
  const isView = mode === 'view';

  const totalQty = f.items.reduce((s, i) => s + (+i.quantity || 0), 0);

  const setItem = (idx, patch) => setF({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  const addItem = () => setF({ ...f, items: [...f.items, { ...emptyItem }] });
  const removeItem = (idx) => setF({ ...f, items: f.items.filter((_, i) => i !== idx) });

  const createPO = async () => {
    if (!f.vendor_id) { toast.error('Choose a vendor'); return; }
    const cleaned = f.items.filter(i => i.chain_name && +i.quantity > 0);
    if (cleaned.length === 0) { toast.error('Add at least one item'); return; }
    setSaving(true);
    try {
      await chainPoApi.createPo({
        vendor_id: f.vendor_id,
        items: cleaned.map(i => ({
          chain_name: i.chain_name, karat: i.karat || '',
          length_inches: i.length_inches === '' ? null : +i.length_inches,
          quantity: +i.quantity,
          notes: i.notes || '',
        })),
        expected_delivery_date: f.expected_delivery_date || null,
        notes: f.notes || '',
      });
      toast.success('PO created as draft'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  const doAction = async (fn, label) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const { data, headers } = await chainPoApi.downloadPoPdf(initial.id);
      const m = (headers['content-disposition'] || '').match(/filename="([^"]+)"/);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url; a.download = m ? m[1] : `PO_${initial.po_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const openReceive = () => {
    // Pre-populate remaining qty per item so partial receipts are quick.
    const alreadyBy = {};
    (initial.receipts || []).forEach(r => (r.items || []).forEach(it => {
      const k = `${it.chain_name}|${it.karat || ''}`;
      alreadyBy[k] = (alreadyBy[k] || 0) + (+it.quantity || 0);
    }));
    setReceiveItems(initial.items.map(it => {
      const k = `${it.chain_name}|${it.karat || ''}`;
      const remaining = Math.max(0, (+it.quantity || 0) - (alreadyBy[k] || 0));
      return { chain_name: it.chain_name, karat: it.karat || '', quantity: remaining };
    }).filter(i => i.quantity > 0));
    setReceiveNote('');
  };

  const submitReceive = async () => {
    const items = receiveItems.filter(i => +i.quantity > 0).map(i => ({
      chain_name: i.chain_name, karat: i.karat, quantity: +i.quantity,
    }));
    if (items.length === 0) { toast.error('Enter at least one received quantity'); return; }
    await doAction(() => chainPoApi.receivePo(initial.id, { items, notes: receiveNote }), 'Receipt recorded');
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isView ? `PO ${initial.po_number}` : 'New Purchase Order'}
            {isView && <Badge className={`${STATUS_STYLES[initial.status]} border text-[10px]`}>{STATUS_LABEL[initial.status]}</Badge>}
            {isView && initial.is_overdue && <Badge className="text-[10px] bg-rose-100 text-rose-700 border-rose-300">Overdue</Badge>}
          </DialogTitle>
        </DialogHeader>

        {isView && receiveItems.length > 0 && (
          <Card className="border-amber-300 bg-amber-50/40">
            <CardContent className="p-3 space-y-2">
              <p className="text-sm font-semibold">Record receipt</p>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-muted-foreground">
                  <tr><th className="text-left py-1">Chain</th><th className="text-left py-1">Karat</th><th className="text-right py-1">Received now</th></tr>
                </thead>
                <tbody>
                  {receiveItems.map((it, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="py-1">{it.chain_name}</td>
                      <td className="py-1">{it.karat || '—'}</td>
                      <td className="py-1 text-right">
                        <Input type="number" value={it.quantity} onChange={(e) => setReceiveItems(receiveItems.map((r, idx) => idx === i ? { ...r, quantity: e.target.value } : r))} className="h-7 w-24 text-right" data-testid={`receive-qty-${i}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Input placeholder="Receipt notes (optional)" value={receiveNote} onChange={(e) => setReceiveNote(e.target.value)} className="h-8" />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitReceive} disabled={busy} data-testid="submit-receive-btn"><PackageCheck className="h-4 w-4 mr-1" />Submit</Button>
                <Button size="sm" variant="ghost" onClick={() => setReceiveItems([])}><X className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        )}

        {!isView && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor *">
              <Select value={f.vendor_id} onValueChange={(v) => setF({ ...f, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Expected Delivery Date"><Input type="date" value={f.expected_delivery_date || ''} onChange={(e) => setF({...f, expected_delivery_date: e.target.value})} data-testid="po-delivery-input" /></Field>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold">Items</p>
            {!isView && <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="h-3.5 w-3.5 mr-1" />Add item</Button>}
          </div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-1.5">Chain</th>
                  <th className="text-left px-2 py-1.5">Karat</th>
                  <th className="text-right px-2 py-1.5">Length (in)</th>
                  <th className="text-right px-2 py-1.5">Qty</th>
                  {!isView && <th className="w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {f.items.map((it, i) => {
                  return (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1"><Input value={it.chain_name} onChange={(e) => setItem(i, { chain_name: e.target.value })} disabled={isView} className="h-7" data-testid={`item-name-${i}`} /></td>
                      <td className="px-2 py-1"><Input value={it.karat} onChange={(e) => setItem(i, { karat: e.target.value })} disabled={isView} className="h-7 w-20" /></td>
                      <td className="px-2 py-1"><Input type="number" value={it.length_inches} onChange={(e) => setItem(i, { length_inches: e.target.value })} disabled={isView} className="h-7 text-right w-24" /></td>
                      <td className="px-2 py-1"><Input type="number" value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} disabled={isView} className="h-7 text-right w-20" data-testid={`item-qty-${i}`} /></td>
                      {!isView && (
                        <td className="px-1">{f.items.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeItem(i)}><X className="h-3.5 w-3.5" /></Button>}</td>
                      )}
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-amber-300 bg-amber-50/50 dark:bg-amber-500/5 font-bold">
                  <td className="px-2 py-1.5" colSpan={3}>TOTAL</td>
                  <td className="px-2 py-1.5 text-right">{totalQty}</td>
                  {!isView && <td></td>}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {isView ? (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-3 text-xs">
              <InfoBox label="Delivery expected" value={formatDateDMY(initial.expected_delivery_date) || '—'} />
              <InfoBox label="Received / Pending" value={`${initial.received_qty} / ${initial.pending_qty}`} />
              <InfoBox label="Created by" value={initial.created_by_name} />
            </div>
            {initial.notes && <p className="text-xs text-muted-foreground border-l-2 border-amber-400 pl-2">{initial.notes}</p>}
            {(initial.receipts || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-1">Receipts</p>
                <div className="space-y-1 max-h-40 overflow-auto">
                  {initial.receipts.map((r, i) => (
                    <div key={i} className="text-xs p-2 rounded border border-border">
                      <span className="font-semibold">{formatDateDMY(r.received_on)}</span> · {r.items.reduce((s, x) => s + (+x.quantity || 0), 0)} pcs by {r.recorded_by_name}
                      {r.notes && <span className="text-muted-foreground"> — {r.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} /></Field>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {isView && (
            <>
              <Button variant="outline" onClick={downloadPdf} data-testid="po-pdf-btn"><Download className="h-4 w-4 mr-1" />PDF</Button>
              {initial.status === 'draft' && (
                <Button onClick={() => doAction(() => chainPoApi.sendPo(initial.id), 'PO sent to vendor')} disabled={busy} data-testid="send-po-btn"><Send className="h-4 w-4 mr-1" />Send PO</Button>
              )}
              {(initial.status === 'sent' || initial.status === 'partially_received') && (
                <>
                  <Button onClick={openReceive} disabled={busy || receiveItems.length > 0} data-testid="record-receipt-btn"><PackageCheck className="h-4 w-4 mr-1" />Record receipt</Button>
                  <Button variant="outline" onClick={() => doAction(() => chainPoApi.remindPo(initial.id), 'Reminder sent')} disabled={busy}><Bell className="h-4 w-4 mr-1" />Remind vendor</Button>
                </>
              )}
              {(initial.status === 'fully_received' || initial.status === 'partially_received') && (
                <Button variant="outline" onClick={() => doAction(() => chainPoApi.closePo(initial.id), 'PO closed')} disabled={busy}><CheckCircle2 className="h-4 w-4 mr-1" />Close PO</Button>
              )}
              {initial.status === 'draft' && (
                <Button variant="outline" className="text-rose-600" onClick={async () => {
                  if (!window.confirm('Delete this draft?')) return;
                  await doAction(() => chainPoApi.deletePo(initial.id), 'PO deleted');
                }}><Trash2 className="h-4 w-4" /></Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!isView && <Button onClick={createPO} disabled={saving} data-testid="save-po-btn">{saving ? 'Saving…' : 'Create draft'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────── Spring Hook Dialog ───────
const SpringHookDialog = ({ vendor, onClose }) => {
  const [log, setLog] = useState({ total_issued: 0, issuances: [] });
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const refresh = async () => {
    try { const { data } = await chainPoApi.listSpringHooks(vendor.id); setLog(data); }
    catch { /* silent */ }
  };
  useEffect(() => { refresh(); }, []);
  const issue = async () => {
    if (!qty || +qty <= 0) { toast.error('Enter a positive quantity'); return; }
    setSaving(true);
    try {
      await chainPoApi.issueSpringHooks(vendor.id, { quantity: +qty, notes: note });
      toast.success('Spring hooks issued'); setQty(''); setNote(''); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Spring Hooks — {vendor.name}</DialogTitle></DialogHeader>
        <Card><CardContent className="p-3">
          <p className="text-[10px] uppercase text-muted-foreground">Total issued to this vendor</p>
          <p className="text-3xl font-bold tabular-nums">{log.total_issued}</p>
        </CardContent></Card>
        <div className="grid grid-cols-3 gap-2 items-end">
          <Field label="Quantity"><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="hook-qty-input" /></Field>
          <div className="col-span-2">
            <Field label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. batch #23" /></Field>
          </div>
        </div>
        <Button onClick={issue} disabled={saving} data-testid="issue-hooks-btn"><Plus className="h-4 w-4 mr-1" />Issue & notify on WhatsApp</Button>
        <div>
          <p className="text-sm font-semibold mb-1">History</p>
          <div className="space-y-1 max-h-56 overflow-auto">
            {log.issuances.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No issuances yet</p>
            ) : log.issuances.map(i => (
              <div key={i.id} className="text-xs p-2 rounded border border-border flex justify-between">
                <span><span className="font-semibold">{i.quantity} pcs</span> · {i.notes || '—'}</span>
                <span className="text-muted-foreground">{formatDateDMY(i.issued_on)} · {i.issued_by_name}</span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold">{label}</Label>
    {children}
  </div>
);
const InfoBox = ({ label, value }) => (
  <div className="p-2 rounded border border-border">
    <p className="text-[9px] uppercase text-muted-foreground">{label}</p>
    <p className="text-sm font-semibold">{value}</p>
  </div>
);
